import type { EulerRotation, Track } from '@mindcloudxai_3d/core';
import { RendererController } from '@renderer/index';
import {
  fetchScene,
  resolveDefaultSceneTrack,
  toDirectSceneConfig,
  toSceneConfig,
} from '@services/scene';
import { initialCameraFromTrack } from '@services/experienceMapper';
import { toViewerAnnotations } from '@/modules/hotspots/data';
import { generateMockTrack } from '@/modules/cinematic/mockTracks';
import { createLogger } from '@utils/logger';
import type { ExploreUrlParams } from '@hooks/useUrlParams';
import type { AccessInfo, SceneConfig, TaskData } from '@/types';
import { sceneBaseRotation } from '../../utils/sceneRotation';

const log = createLogger('SceneSession');

// ===========================================================================
// 场景控制 — 判定「加载什么、怎么加载」，只生成配置或调用 renderer 加载。
// ===========================================================================

export interface ResolveSceneConfigOptions {
  sceneId: string | null | undefined;
  directUrl: string | null;
  taskCode: string | null;
  taskId: number | null;
  signal: AbortSignal;
}

export interface ResolveSceneConfigResult {
  config: SceneConfig;
  baseRotation: EulerRotation | undefined;
  task: TaskData | null;
  access: AccessInfo | null;
}

/** 场景数据获取 + config 构建，抽离为独立函数方便测试。 */
export async function resolveSceneConfig(
  opts: ResolveSceneConfigOptions,
): Promise<ResolveSceneConfigResult> {
  if (opts.sceneId) {
    const result = await fetchScene({
      taskCode: opts.taskCode ?? undefined,
      taskId: opts.taskId ?? undefined,
      signal: opts.signal,
    });
    const task = result.task;

    let config = toSceneConfig(task);
    if (!config.asset.url) {
      log.error('No splat URL resolved from task data', {
        output_result: JSON.stringify(task.output_result, null, 2),
        status: task.status,
      });
      throw new Error(
        `Scene has no splat data (task status=${task.status}, code=${task.task_code})`,
      );
    }

    const baseRotation = sceneBaseRotation(task.is_yup ?? false);

    if (!config.track) {
      const defaultTrack = await resolveDefaultSceneTrack(task, { signal: opts.signal });
      if (defaultTrack) {
        const trackCamera = initialCameraFromTrack(defaultTrack);
        config = {
          ...config,
          track: defaultTrack,
          initialCamera: trackCamera ?? config.initialCamera,
        };
      }
    }

    return { config, baseRotation, task, access: result.access ?? null };
  }

  if (opts.directUrl) {
    return {
      config: toDirectSceneConfig(opts.directUrl),
      baseRotation: sceneBaseRotation(true),
      task: null,
      access: null,
    };
  }

  throw new Error('No scene ID or URL provided');
}

/** mock 优先，空帧轨迹视为无效。 */
export function resolveTrack(
  mockType: ExploreUrlParams['mockTrack'],
  duration: number,
  center: ExploreUrlParams['trackCenter'],
  configTrack: Track | undefined,
): Track | null {
  const track = mockType
    ? generateMockTrack(mockType, { duration, center: center ?? undefined })
    : (configTrack ?? null);
  if (!track || track.keyframes.times.length === 0) return null;
  return track;
}

export function resolveInitialHotspotIndex(
  config: SceneConfig,
  hotspotId: string | null,
): number | null {
  if (!hotspotId) return null;
  const index = config.hotspots.findIndex((h) => h.id === hotspotId);
  return index >= 0 ? index : null;
}

export interface LoadRendererSceneOptions {
  config: SceneConfig;
  track: Track | null;
  collisionUrl: string | null;
  debugCollision: boolean;
  debugTrackRoute: boolean;
  baseRotation: EulerRotation | undefined;
}

/** 组装 SceneConfig → RendererController.loadScene 入参，隔离 viewer 加载契约细节。 */
export function loadRendererScene(
  ctrl: RendererController,
  opts: LoadRendererSceneOptions,
): Promise<void> {
  const { config, track, collisionUrl, debugCollision, debugTrackRoute, baseRotation } = opts;
  return ctrl.loadScene({
    url: config.asset.url,
    sogMeta: config.asset.sogMeta,
    settings: config.experienceSettings,
    annotations: toViewerAnnotations(config.hotspots),
    animTrack: track ?? undefined,
    autoPlayAnim: !!track,
    // 直链 collisionUrl 已加签时跳过 config.collisionBinUrl 推导，避免拉到未签名 bin。
    collisionUrl: collisionUrl ?? config.collisionUrl,
    collisionBinUrl: collisionUrl ? undefined : config.collisionBinUrl,
    debugCollision,
    debugTrackRoute,
    baseRotation,
  });
}
